import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { Button, ErrorState, Field, Input, Select, Textarea } from '../ui.js'
import type { JobVisit } from './types.js'
import { dateInputValue } from '../../lib/format.js'

/**
 * Editing a booking without leaving the job.
 *
 * The board can already move a visit by dragging it, which is faster for
 * "half an hour later" and useless for "same time, different technician, and
 * tell them why". This is the second half of that.
 *
 * Two mutations rather than one, because the API splits them: `moveVisit`
 * owns the times and the assignment and reports conflicts; `updateVisit` owns
 * the status and the note. Only the ones that actually changed are sent.
 *
 * The office decides one thing about a booking's status: whether the customer
 * has agreed to the time. En route, on site and completed are the technician's
 * own buttons, so they are reported here and never picked.
 */

const DURATIONS = [30, 45, 60, 90, 120, 180, 240, 480]

const PROGRESS_LABELS: Record<string, string> = {
  EN_ROUTE: 'Technician on the way',
  ON_SITE: 'Technician on site',
  COMPLETED: 'Visit completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No show',
}

export function BookingPopup({
  open,
  visit,
  jobId,
  jobNumber,
  onClose,
}: {
  open: boolean
  /** Null to make a new booking on `jobId`. */
  visit: JobVisit | null
  jobId: string
  jobNumber: string
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="booking-popup-title" className="max-w-[520px]">
      {/*
        Keyed, because the form reads its date, its length and its technician
        into state once. Without it, editing a second booking opens on the
        first one's times — and the Save that follows writes them.
      */}
      {open ? (
        <BookingForm
          key={visit?.id ?? 'new'}
          visit={visit}
          jobId={jobId}
          jobNumber={jobNumber}
          onClose={onClose}
        />
      ) : null}
    </Modal>
  )
}

/**
 * The booking editor itself, always inside `BookingPopup`.
 *
 * A null `visit` is the same form making a booking rather than changing one:
 * the office answering "when can you come" on the phone has the job open in
 * front of them, and sending them to the board to draw a slot loses the call.
 * The write is `createVisit`, which is also what moves the job out of the
 * backlog, so this is not a second way of booking — it is the same one.
 */
export function BookingForm({
  visit,
  jobId,
  jobNumber,
  onClose,
}: {
  /** Null to make a new booking on `jobId`. */
  visit: JobVisit | null
  jobId: string
  jobNumber: string
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const people = useQuery(trpc.users.schedulable.queryOptions())

  const [date, setDate] = useState(() => dateInputValue(visit?.startAt ?? nextWorkingMorning()))
  const [time, setTime] = useState(() =>
    visit ? isoTime(visit.startAt) : isoTime(nextWorkingMorning()),
  )
  const [minutes, setMinutes] = useState(() => (visit ? lengthOf(visit) : 60))
  const [userId, setUserId] = useState(
    () => (visit?.assignments.find((row) => row.isLead) ?? visit?.assignments[0])?.userId ?? '',
  )
  // A booking being made here has not been agreed to either. Decision 76.
  const wasConfirmed = visit?.status === 'CONFIRMED'
  const progress = visit ? (PROGRESS_LABELS[visit.status] ?? null) : null
  const [confirmed, setConfirmed] = useState(wasConfirmed)
  const [notes, setNotes] = useState(visit?.notes ?? '')

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }

  const create = useMutation(trpc.schedule.createVisit.mutationOptions())
  const move = useMutation(trpc.schedule.moveVisit.mutationOptions())
  const update = useMutation(trpc.schedule.updateVisit.mutationOptions())
  const unbook = useMutation(
    trpc.schedule.deleteVisit.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )

  const startAt = new Date(`${date}T${time}`)
  const valid = !Number.isNaN(startAt.getTime()) && minutes > 0
  const error = create.error ?? move.error ?? update.error ?? unbook.error

  const save = async () => {
    if (!valid) return

    const endAt = new Date(startAt.getTime() + minutes * 60_000)

    if (!visit) {
      await create.mutateAsync({
        jobId,
        startAt,
        endAt,
        userIds: userId ? [userId] : [],
        status: confirmed ? 'CONFIRMED' : 'PROPOSED',
        ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
      })

      invalidate()
      onClose()
      return
    }

    const originalUserId =
      (visit.assignments.find((row) => row.isLead) ?? visit.assignments[0])?.userId ?? ''

    const timesChanged =
      startAt.getTime() !== visit.startAt.getTime() || minutes !== lengthOf(visit)

    const confirmationChanged = progress === null && confirmed !== wasConfirmed
    const writeStatus = async () => {
      if (!confirmationChanged && notes === (visit.notes ?? '')) return
      await update.mutateAsync({
        id: visit.id,
        ...(confirmationChanged ? { status: confirmed ? 'CONFIRMED' : 'PROPOSED' } : {}),
        notes: notes.trim() === '' ? null : notes.trim(),
      })
    }

    // A confirmed booking refuses to move, so unticking has to land before the
    // new time does and ticking after it.
    if (wasConfirmed && !confirmed) await writeStatus()

    if (timesChanged || userId !== originalUserId) {
      await move.mutateAsync({
        id: visit.id,
        startAt,
        endAt,
        ...(userId ? { userIds: [userId] } : {}),
      })
    }

    if (!(wasConfirmed && !confirmed)) await writeStatus()

    invalidate()
    onClose()
  }

  return (
    <>
      <DialogHeader
        id="booking-popup-title"
        title={visit ? 'Edit booking' : 'New booking'}
        aside={`Job ${jobNumber}`}
        onClose={onClose}
      />

      <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2">
        <Field label="Date" htmlFor="booking-date">
          <Input
            id="booking-date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>

        <Field label="Start" htmlFor="booking-start">
          <Input
            id="booking-start"
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
        </Field>

        <Field label="Length" htmlFor="booking-length">
          <Select
            id="booking-length"
            value={minutes}
            onChange={(event) => setMinutes(Number(event.target.value))}
          >
            {/* A booking imported from ServiceM8 can be any length at all, so
                its own is offered rather than snapped to the nearest option. */}
            {[...new Set(visit ? [...DURATIONS, lengthOf(visit)] : DURATIONS)]
              .sort((a, b) => a - b)
              .map((option) => (
                <option key={option} value={option}>
                  {option < 60 ? `${option} min` : `${option / 60} hr`}
                </option>
              ))}
          </Select>
        </Field>

        <Field label="Technician" htmlFor="booking-technician">
          <Select
            id="booking-technician"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">Unassigned</option>
            {(people.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="sm:col-span-2">
          {progress ? (
            <p className="text-sm text-ink-600">{progress}</p>
          ) : (
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                className="size-4 accent-ink-900"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              Time confirmed with the customer
            </label>
          )}
        </div>

        <div className="sm:col-span-2">
          <Field label="Note to the technician" htmlFor="booking-notes">
            <Textarea
              id="booking-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Anything they need to know before they arrive"
            />
          </Field>
        </div>

        {error ? (
          <div className="sm:col-span-2">
            <ErrorState message={errorMessage(error)} />
          </div>
        ) : null}
      </div>

      <DialogFooter
        // Unbooking is a smaller thing than it looks: the job stays, the
        // block leaves the board, and the rail picks it back up.
        start={
          visit ? (
            <Button
              size="sm"
              variant="ghost"
              loading={unbook.isPending}
              onClick={() => unbook.mutate({ id: visit.id })}
            >
              Unbook
            </Button>
          ) : null
        }
      >
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!valid}
          loading={create.isPending || move.isPending || update.isPending}
          onClick={() => void save()}
        >
          {visit ? 'Save booking' : 'Book it in'}
        </Button>
      </DialogFooter>
    </>
  )
}

const pad = (value: number) => String(value).padStart(2, '0')

function isoTime(value: Date): string {
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`
}

function lengthOf(visit: JobVisit): number {
  return Math.round((visit.endAt.getTime() - visit.startAt.getTime()) / 60_000)
}

/**
 * Where a new booking starts before anybody touches the form: eight in the
 * morning, the next day that is not a weekend. Today is deliberately not
 * offered as the default — a booking made for a slot that has already been
 * driven past is the one mistake this form can make on its own.
 */
function nextWorkingMorning(): Date {
  const day = new Date()
  day.setDate(day.getDate() + 1)
  while (day.getDay() === 0 || day.getDay() === 6) day.setDate(day.getDate() + 1)
  day.setHours(8, 0, 0, 0)
  return day
}
